import type { Segment } from "../types"
import type { CompanyDirectoryEntry } from "./segment-connections"

import { COMPANY_NAME } from "./branding"
import { segmentAddressableTotal } from "./segment-analysis-utils"

const BOM = "﻿"

function csvCell(value: string | number): string {
  let text = String(value)
  // Neutralise CSV formula injection (CWE-1236): a string cell beginning with
  // =,+,-,@,tab,CR is interpreted as a formula by Excel/Sheets. Names are
  // user-controlled, so prefix a single quote to keep such cells literal.
  // (Numbers are exempt so legitimate negatives aren't mangled.)
  if (typeof value === "string" && /^[=+\-@\t\r]/.test(text)) text = `'${text}`
  return /[",\n\r]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text
}

export function rowsToCsv(rows: Array<Array<string | number>>): string {
  return rows.map((row) => row.map(csvCell).join(",")).join("\r\n")
}

export function segmentsCsv(segments: Segment[]): string {
  const header = [
    "Segment",
    "Owner",
    "Market",
    "Addressable market (MEUR)",
    "CAGR 5y (%)",
    `${COMPANY_NAME} sales (MEUR)`,
    "Share (%)",
    "Status",
    "Last run",
  ]
  const rows = segments.map((segment) => {
    const am = segmentAddressableTotal(segment)
    const share = am > 0 ? Math.round((segment.sales / am) * 1000) / 10 : segment.share
    return [
      segment.name,
      segment.owner,
      segment.marketName,
      am,
      segment.cagr,
      segment.sales,
      share,
      segment.runStatus,
      segment.lastRunDate,
    ]
  })
  return rowsToCsv([header, ...rows])
}

export function companiesCsv(directory: CompanyDirectoryEntry[]): string {
  const header = ["Company", "Roles", "Segments", "Markets"]
  const rows = directory.map((entry) => [
    entry.name,
    entry.roles.join(" / "),
    entry.segments.map((segment) => segment.name).join("; "),
    Array.from(new Set(entry.segments.map((segment) => segment.marketName))).join("; "),
  ])
  return rowsToCsv([header, ...rows])
}

/** Trigger a client-side CSV download (BOM-prefixed so Excel reads UTF-8). */
export function downloadCsv(filename: string, csv: string): void {
  const blob = new Blob([BOM + csv], { type: "text/csv;charset=utf-8;" })
  const url = URL.createObjectURL(blob)
  const link = document.createElement("a")
  link.href = url
  link.download = filename
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}
